import { RapierRigidBody, RigidBody, vec3 } from "@react-three/rapier";
import { MeshBasicMaterial, Vector3 } from "three";
import { useRef, useEffect } from "react";
import { useFrame } from "@react-three/fiber";
import { FakeGlowMaterial } from "./FakeGlowMaterial";
import { Sphere } from "@react-three/drei";

export const SOL_SPELL_OFFSET = new Vector3(-0.8, 0.1, -0.8);
export const ENTITY_SPELL_OFFSET = new Vector3(0, 0.1, 0);
const SPELL_SPEED = 20;
const SPELL_RANGE = 15;

const spellMaterial = new MeshBasicMaterial({
  color: "#446ED1",
  toneMapped: false,
});

interface SpellProps {
  caster?: string;
  angle?: number;
  position?: Vector3;
  onHit: (vector: Vector3) => void;
}

const Spell = ({ caster, angle, position, onHit }: SpellProps) => {
  const spellRef = useRef<RapierRigidBody>(null);
  const spellMeshRef = useRef<any>(null);

  useEffect(() => {
    /**
     * Apply fixed speed, rotation and translation to the spell
     */
    const velocity = {
      x: Math.sin(angle!) * SPELL_SPEED,
      y: 0,
      z: Math.cos(angle!) * SPELL_SPEED,
    };
    spellRef.current!.lockRotations(true, false);
    spellRef.current!.lockTranslations(true, false);
    spellRef.current!.setLinvel(velocity, false);
  }, [angle]);

  useFrame((state, delta) => {
    /**
     * End the spell if it does not hit anything within a given distance
     */
    const originPos = position!;
    const currentPos = spellMeshRef.current!.getWorldPosition(new Vector3());
    if (getDistanceToCaster(originPos, currentPos) >= SPELL_RANGE) {
      endSpell();
    }
  });

  /**
   * Caltulate the distance between the origin point and the current position
   * @param originPos The position where the spell was casted
   * @param currentPos The position where the spell is in a given frame
   * @returns The distance between the two points
   */
  const getDistanceToCaster = (originPos: Vector3, currentPos: Vector3) => {
    const distance = originPos.distanceTo(currentPos);
    return distance;
  };

  /**
   * Terminate the spell and perform cleanup operations.
   * This function disables the spell's functionality and triggers the 'onHit' function
   * with the current translation vector of the spell.
   */
  const endSpell = () => {
    spellRef.current!.setEnabled(false);
    onHit(vec3(spellRef.current!.translation()));
  };

  return (
    <group position={position} rotation-y={angle}>
      <group position={caster === "Sol" ? SOL_SPELL_OFFSET : ENTITY_SPELL_OFFSET}>
        <RigidBody
          ref={spellRef}
          name={caster + "Spell"}
          gravityScale={0}
          onCollisionEnter={({ other }) => {
            if (
              (other.rigidBodyObject!.userData.type !== "spell" ||
                other.rigidBodyObject!.name !== "") &&
              other.rigidBodyObject!.name !== caster
            ) {
              endSpell();
            }
          }}
          userData={{
            type: "spell",
          }}>
          <group ref={spellMeshRef}>
            <Sphere position={[0, 0, 0]} args={[0.3]}>
              <FakeGlowMaterial />
            </Sphere>
            <mesh position-z={0} material={spellMaterial} castShadow>
              <sphereGeometry args={[0.15]} />
            </mesh>
          </group>
        </RigidBody>
      </group>
    </group>
  );
};

export default Spell;
